import { BOGGLE_BADGE_DEFINITIONS } from '~/boggle/config/badges/registry'
import type { BoggleBadgeLevel } from '~/boggle/config/badges/types'
import type {
  BoggleAchievementDefinition,
  BoggleAchievementRequirement,
  BoggleAchievementTierDefinition,
} from '~/boggle/config/achievements/types'
import type { BoggleBadgeProgressProjection } from '~/boggle/progress/types'
import type { BoggleAchievementCareerStats } from './careerStats'
import { boggleCareerStatsHasReachedRating } from './careerStats'

export type BoggleAchievementEvaluationContext = {
  badgeProgress: BoggleBadgeProgressProjection
  careerStats: BoggleAchievementCareerStats
}

export type BoggleAchievementTierProgress = {
  completed: boolean
  current: number
  target: number
}

function badgeDefinitionsForLevel(level: BoggleBadgeLevel) {
  return BOGGLE_BADGE_DEFINITIONS.filter(definition => definition.level === level)
}

function countUnlockedBadgesForLevel(
  level: BoggleBadgeLevel,
  badgeProgress: BoggleBadgeProgressProjection,
) {
  return badgeDefinitionsForLevel(level).filter(
    definition => badgeProgress.perBadge[definition.id],
  ).length
}

export function getBoggleAchievementRequirementProgress(
  requirement: BoggleAchievementRequirement,
  context: BoggleAchievementEvaluationContext,
): BoggleAchievementTierProgress {
  switch (requirement.type) {
    case 'all_badges_of_level': {
      const target = badgeDefinitionsForLevel(requirement.level).length
      const current = countUnlockedBadgesForLevel(
        requirement.level,
        context.badgeProgress,
      )
      return { completed: target > 0 && current >= target, current, target }
    }
    case 'completed_runs':
      return {
        completed: context.careerStats.completedRunCount >= requirement.count,
        current: context.careerStats.completedRunCount,
        target: requirement.count,
      }
    case 'first_badge_of_level': {
      const current =
        countUnlockedBadgesForLevel(requirement.level, context.badgeProgress) > 0 ? 1 : 0
      return { completed: current >= 1, current, target: 1 }
    }
    case 'long_words': {
      const current = context.careerStats.longWordCounts[requirement.minLength] ?? 0
      return {
        completed: current >= requirement.count,
        current,
        target: requirement.count,
      }
    }
    case 'reached_rating': {
      const current = boggleCareerStatsHasReachedRating(
        context.careerStats,
        requirement.ratingId,
      )
        ? 1
        : 0
      return { completed: current >= 1, current, target: 1 }
    }
    case 'single_run_word_count':
      return {
        completed: context.careerStats.maxRunWordCount >= requirement.count,
        current: context.careerStats.maxRunWordCount,
        target: requirement.count,
      }
    case 'total_words':
      return {
        completed: context.careerStats.totalWords >= requirement.count,
        current: context.careerStats.totalWords,
        target: requirement.count,
      }
  }
}

export function evaluateBoggleAchievementTier(
  tier: BoggleAchievementTierDefinition,
  context: BoggleAchievementEvaluationContext,
) {
  return getBoggleAchievementRequirementProgress(tier.requirement, context).completed
}

export function evaluateBoggleAchievements(
  definitions: readonly BoggleAchievementDefinition[],
  context: BoggleAchievementEvaluationContext,
) {
  return definitions.flatMap(definition =>
    definition.tiers
      .filter(tier => evaluateBoggleAchievementTier(tier, context))
      .map(tier => tier.id),
  )
}
